/**
 * Page Classement des entreprises
 *
 * Affiche les entreprises classées selon la note moyenne Job-Nutriscore de leurs offres
 * Product Manager. Les offres sont dédupliquées (même poste sur plusieurs sources).
 * Les pertes économiques sont calculées pour les offres françaises notées D ou E.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { app, analytics } from '../index';
import { useTranslation } from 'react-i18next';
import { hasFlag } from 'country-flag-icons';
import Flags from 'country-flag-icons/react/3x2';
import './ClassementEntreprises.css';
import NutriScoreBadge from './NutriScoreBadge';

const PAGE_SIZE = 20;
const NUTRI_SCORE_LABELS = { 5: 'A', 4: 'B', 3: 'C', 2: 'D', 1: 'E' };

// Scénario réaliste - perte sur 10 ans (article "The Hidden Cost of a Misleading Product Manager Title")
const BASE_PERTE_SALARIE = 297500; // €285k–€310k realistic

// Perte État (The Hidden Cost of Misleading Job Titles for the State, conservative ~€985k per startup)
// Part fixe par startup : soutien direct, perte impôt société, perte PIB/FDI
const ETAT_DIRECT_SUPPORT = 400000;   // €400k grants, loans, tax breaks, incubator
const ETAT_CORPORATE_TAX_LOSS = 125000; // €125k corporate tax loss
const ETAT_GDP_FDI_LOSS = 250000;     // €250k productivity / FDI
// Part proportionnelle à N (nb d'offres FR D/E) : formation, impôt sur le revenu, chômage
const ETAT_PER_OFFER_TRAINING = 80000;   // €80k training + wasted training (réparti par offre)
const ETAT_PER_OFFER_INCOME_TAX = 40000; // €40k income tax & social contribution loss
const ETAT_PER_OFFER_UNEMPLOYMENT = 90000; // €90k unemployment & welfare
const ETAT_BASE_PER_STARTUP = ETAT_DIRECT_SUPPORT + ETAT_CORPORATE_TAX_LOSS + ETAT_GDP_FDI_LOSS; // €775k
const ETAT_PER_OFFER = ETAT_PER_OFFER_TRAINING + ETAT_PER_OFFER_INCOME_TAX + ETAT_PER_OFFER_UNEMPLOYMENT; // €210k

// Estimation du gain moyen pour un intermédiaire (commission cumulée par offre)
// Hypothèses marché FR pour postes Product Owner / Product Manager, mélange freelance + CDI.
// Freelance : marge journalière * durée moyenne d'une mission
const FREELANCE_DAILY_MARGIN = 200;   // marge moyenne/jour facturé (€/jour)
const FREELANCE_MISSION_DAYS = 18 * 18; // ~18 mois * 18 jours facturés/mois ≈ 324 jours
const FREELANCE_COMMISSION = FREELANCE_DAILY_MARGIN * FREELANCE_MISSION_DAYS; // ≈ 64 800 €

// CDI : % du salaire brut annuel sur la durée moyenne de maintien en poste
const CDI_ANNUAL_GROSS = 65000;       // salaire brut moyen (€/an)
const CDI_TENURE_YEARS = 3;           // durée moyenne en poste (années)
const CDI_COMMISSION_RATE = 0.20;     // 20 % du brut annuel
const CDI_COMMISSION = CDI_ANNUAL_GROSS * CDI_COMMISSION_RATE * CDI_TENURE_YEARS; // ≈ 39 000 €

// Mix estimatif 60 % freelance / 40 % CDI
const FREELANCE_SHARE = 0.6;
const CDI_SHARE = 0.4;

const AVERAGE_INTERMEDIARY_COMMISSION =
  FREELANCE_SHARE * FREELANCE_COMMISSION +
  CDI_SHARE * CDI_COMMISSION; // ≈ 60–65 k€ par offre
const WEIGHT_E = 1;
const WEIGHT_D = 0.8;

function getScoreWeight(score) {
  const rounded = Math.round(score);
  if (rounded <= 1) return WEIGHT_E;
  if (rounded <= 2) return WEIGHT_D;
  return 0;
}

function formatEuro(value) {
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)} M€`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(0)} k€`;
  return `${Math.round(value)} €`;
}

/** Clé normalisée pour regrouper par entreprise (casse, trim). */
function normalizeCompanyKey(name) {
  if (!name || typeof name !== 'string') return '';
  return name.trim().toLowerCase();
}

/** Clé pour la lookup dans normalizedCompanies (accents retirés, majuscules, sans espaces). */
function normalizeKeyForLookup(str) {
  if (!str || typeof str !== 'string') return '';
  const normalized = str.trim().toUpperCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
  return normalized.replace(/\s+/g, '');
}

/**
 * Clé normalisée pour le poste : permet de fusionner "Product Manager F/H" et "Product Manager H/F"
 * (variantes de genre), et d'ignorer accents, tirets, ponctuation.
 */
function normalizePositionKey(str) {
  if (!str || typeof str !== 'string') return '';
  let s = str.trim().toLowerCase();
  // Variantes de genre (F/H, H/F, M/F, M/W, etc.)
  s = s.replace(/\s*[(\s-]*[fhmw]\/[fhmw][)\s-]*/gi, ' ');
  // Normaliser les accents
  s = s.normalize('NFD').replace(/\p{Diacritic}/gu, '');
  // Normaliser les tirets (hyphen, en dash, em dash)
  s = s.replace(/[\u002D\u2010\u2011\u2012\u2013\u2014\u2015]/g, ' ');
  // Supprimer la ponctuation finale
  s = s.replace(/[.,;:!?]+$/g, '');
  return s.replace(/\s+/g, ' ').trim();
}

const QUERY_PARAMS_TO_STRIP = new Set([
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'fbclid', 'gclid', 'gclsrc', 'msclkid', 'tt_medium', 'tt_content',
  'ref', 'ref_src', 'ref_url', 'ref_campaign', 'referrer',
  'share', 'shareIntent', '_ga', '_gl', 'mc_cid', 'mc_eid',
  'xtor', 'xtref', 'xtrfr',
]);

/**
 * URL normalisée pour déduplication (identique à analyzeJobOffer/normalizeUrl) :
 * - Retire le hash (#) et les paramètres inutiles (UTM, tracking) ; conserve les autres
 * - LinkedIn : fusionne view/ID, search?currentJobId=ID, etc. vers jobs/view/ID
 */
function normalizeUrlForDedup(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    const u = new URL(url.trim());
    u.hash = '';
    const host = u.hostname.toLowerCase();
    const isLinkedIn = host === 'linkedin.com' || host.endsWith('.linkedin.com');
    if (isLinkedIn) {
      const pathMatch = u.pathname.match(/\/jobs\/view\/(\d+)\/?$/i);
      if (pathMatch) return `https://www.linkedin.com/jobs/view/${pathMatch[1]}`;
      const jobId = u.searchParams.get('currentJobId') || u.searchParams.get('currentjobid');
      if (jobId && /^\d+$/.test(String(jobId))) return `https://www.linkedin.com/jobs/view/${jobId}`;
    }
    for (const key of [...u.searchParams.keys()]) {
      const lower = key.toLowerCase();
      if (QUERY_PARAMS_TO_STRIP.has(lower) || lower.startsWith('utm_') || lower.startsWith('_ga')) {
        u.searchParams.delete(key);
      }
    }
    let href = u.href;
    if (href.endsWith('/') && u.pathname !== '/') href = href.slice(0, -1);
    return href.toLowerCase();
  } catch {
    return '';
  }
}

/** Host extrait de l'URL pour détecter les offres cross-site */
function getHostFromUrl(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    return new URL(url.trim()).host.toLowerCase();
  } catch {
    return '';
  }
}

function LocationFlagIcon({ code, title, className }) {
  if (!code || typeof code !== 'string') return null;
  const normalized = code.trim().toLowerCase();
  const upper = normalized.toUpperCase();
  if (normalized === 'remote') {
    return (
      <span className={className} title={title || 'Remote'} role="img" aria-label="Remote">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="1.5em" height="1.5em" style={{ verticalAlign: 'middle' }}>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
        </svg>
      </span>
    );
  }
  const flagCode = upper === 'UK' ? 'GB' : upper;
  if (flagCode.length === 2 && hasFlag(flagCode)) {
    const FlagComponent = Flags[flagCode];
    if (FlagComponent) {
      return <FlagComponent title={title || upper} className={className} style={{ height: '1.2em', width: 'auto' }} />;
    }
  }
  return null;
}

// Continent codes: EU, NA, SA, AS, AF, OC
const COUNTRY_TO_CONTINENT = {
  AD: 'EU', AE: 'AS', AF: 'AS', AG: 'NA', AI: 'NA', AL: 'EU', AM: 'AS', AO: 'AF', AR: 'SA', AT: 'EU', AU: 'OC',
  UK: 'EU',
  AZ: 'AS', BA: 'EU', BB: 'NA', BD: 'AS', BE: 'EU', BF: 'AF', BG: 'EU', BH: 'AS', BI: 'AF', BJ: 'AF', BN: 'AS',
  BO: 'SA', BR: 'SA', BS: 'NA', BT: 'AS', BW: 'AF', BY: 'EU', BZ: 'NA', CA: 'NA', CD: 'AF', CF: 'AF', CG: 'AF',
  CH: 'EU', CI: 'AF', CL: 'SA', CM: 'AF', CN: 'AS', CO: 'SA', CR: 'NA', CU: 'NA', CV: 'AF', CY: 'EU', CZ: 'EU',
  DE: 'EU', DJ: 'AF', DK: 'EU', DO: 'NA', DZ: 'AF', EC: 'SA', EE: 'EU', EG: 'AF', ER: 'AF', ES: 'EU', ET: 'AF',
  FI: 'EU', FJ: 'OC', FM: 'OC', FR: 'EU', GA: 'AF', GB: 'EU', GE: 'AS', GH: 'AF', GM: 'AF', GN: 'AF', GQ: 'AF',
  GR: 'EU', GT: 'NA', GW: 'AF', GY: 'SA', HK: 'AS', HN: 'NA', HR: 'EU', HT: 'NA', HU: 'EU', ID: 'AS', IE: 'EU',
  IL: 'AS', IN: 'AS', IQ: 'AS', IR: 'AS', IS: 'EU', IT: 'EU', JM: 'NA', JO: 'AS', JP: 'AS', KE: 'AF', KG: 'AS',
  KH: 'AS', KI: 'OC', KM: 'AF', KN: 'NA', KP: 'AS', KR: 'AS', KW: 'AS', KZ: 'AS', LA: 'AS', LB: 'AS', LC: 'NA',
  LI: 'EU', LK: 'AS', LR: 'AF', LS: 'AF', LT: 'EU', LU: 'EU', LV: 'EU', LY: 'AF', MA: 'AF', MC: 'EU', MD: 'EU',
  ME: 'EU', MG: 'AF', MH: 'OC', MK: 'EU', ML: 'AF', MM: 'AS', MN: 'AS', MR: 'AF', MT: 'EU', MU: 'AF', MV: 'AS',
  MW: 'AF', MX: 'NA', MY: 'AS', MZ: 'AF', NA: 'AF', NE: 'AF', NG: 'AF', NI: 'NA', NL: 'EU', NO: 'EU', NP: 'AS',
  NR: 'OC', NZ: 'OC', OM: 'AS', PA: 'NA', PE: 'SA', PF: 'OC', PG: 'OC', PH: 'AS', PK: 'AS', PL: 'EU', PT: 'EU',
  PW: 'OC', PY: 'SA', QA: 'AS', RO: 'EU', RS: 'EU', RU: 'EU', RW: 'AF', SA: 'AS', SB: 'OC', SC: 'AF', SD: 'AF',
  SE: 'EU', SG: 'AS', SI: 'EU', SK: 'EU', SL: 'AF', SM: 'EU', SN: 'AF', SO: 'AF', SR: 'SA', SS: 'AF', ST: 'AF',
  SV: 'NA', SY: 'AS', SZ: 'AF', TD: 'AF', TG: 'AF', TH: 'AS', TJ: 'AS', TL: 'AS', TM: 'AS', TN: 'AF', TO: 'OC',
  TR: 'AS', TT: 'NA', TV: 'OC', TW: 'AS', TZ: 'AF', UA: 'EU', UG: 'AF', US: 'NA', UY: 'SA', UZ: 'AS', VA: 'EU',
  VC: 'NA', VE: 'SA', VN: 'AS', VU: 'OC', WS: 'OC', XK: 'EU', YE: 'AS', ZA: 'AF', ZM: 'AF', ZW: 'AF'
};

const CONTINENT_IDS = ['EU', 'NA', 'SA', 'AS', 'AF', 'OC'];

function getContinentForCountry(code) {
  if (!code || code === 'remote') return null;
  return COUNTRY_TO_CONTINENT[code.toUpperCase()] || null;
}

function getLocationLabel(code) {
  if (!code || typeof code !== 'string') return code;
  const upper = code.toUpperCase().trim();
  if (upper === 'REMOTE') return 'Remote';
  return upper;
}

function ClassementEntreprises() {
  const { t, i18n } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [selectedCompanyType, setSelectedCompanyType] = useState('');
  const [selectedFilter, setSelectedFilter] = useState(() =>
    (typeof i18n.language === 'string' && i18n.language.toLowerCase().startsWith('fr')) ? 'FR' : ''
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [rawOffers, setRawOffers] = useState([]);
  const [promotionMap, setPromotionMap] = useState({});
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [promoteDialogData, setPromoteDialogData] = useState(null);
  const [offersDialogOpen, setOffersDialogOpen] = useState(false);
  const [offersDialogData, setOffersDialogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const handleLocationFilter = useCallback((value) => {
    setSelectedFilter(value);
    setDropdownOpen(false);
    logEvent(analytics, 'classement_filter_location', { value: value || 'all' });
  }, []);

  const handleCompanyTypeChange = useCallback((e) => {
    const value = e.target.value;
    setSelectedCompanyType(value);
    logEvent(analytics, 'classement_filter_company_type', { value: value || 'all' });
  }, []);

  const handlePagePrev = useCallback(() => {
    const next = Math.max(1, currentPage - 1);
    setCurrentPage(next);
    logEvent(analytics, 'classement_pagination', { direction: 'prev', page: next });
  }, [currentPage]);

  const handlePageNext = useCallback(() => {
    const totalPages = Math.ceil(companies.length / PAGE_SIZE);
    const next = Math.min(totalPages, currentPage + 1);
    setCurrentPage(next);
    logEvent(analytics, 'classement_pagination', { direction: 'next', page: next });
  }, [companies.length, currentPage]);

  const handlePromoteDialogOpen = useCallback((company, links) => {
    setPromoteDialogData({ company, links });
    setPromoteDialogOpen(true);
    logEvent(analytics, 'classement_promote_dialog_open', { company });
  }, []);

  const handlePromoteDialogClose = useCallback(() => {
    setPromoteDialogOpen(false);
    logEvent(analytics, 'classement_promote_dialog_close', {});
  }, []);

  const handlePromoteLinkClick = useCallback((company, linkUrl) => {
    logEvent(analytics, 'classement_promote_link_click', { company, link_url: linkUrl });
  }, []);

  const handleOffersDialogOpen = useCallback((company, offers) => {
    setOffersDialogData({ company, offers });
    setOffersDialogOpen(true);
    logEvent(analytics, 'classement_offers_dialog_open', { company });
  }, []);

  const handleOffersDialogClose = useCallback(() => {
    setOffersDialogOpen(false);
    logEvent(analytics, 'classement_offers_dialog_close', {});
  }, []);

  // Chargement initial : jobOffers, normalizedCompanies (CAC40/French Tech), communicationVerifications
  useEffect(() => {
    const fetchAndRank = async () => {
      try {
        setLoading(true);
        setError(null);
        const db = getFirestore(app);

        const [jobOffersSnap, normalizedSnap, promotionSnap] = await Promise.all([
          getDocs(collection(db, 'jobOffers')),
          getDocs(collection(db, 'normalizedCompanies')),
          getDocs(collection(db, 'communicationVerifications'))
        ]);

        const promoMap = {};
        promotionSnap.docs.forEach((docSnap) => {
          const d = docSnap.data();
          if (docSnap.id) promoMap[docSnap.id] = { promoteResponsible: !!d.promoteResponsible, links: d.promoteResponsibleLinks || [] };
        });
        setPromotionMap(promoMap);

        // Mapping entreprise -> attributs (CAC40, French Tech, type, etc.) et chapeau (nom groupe)
        const attrsMap = {};
        const chapeauMap = {};
        normalizedSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const attrs = {
            CAC40: !!data.CAC40,
            FrenchTechNext40120: !!data.FrenchTechNext40120,
            companyType: data.companyType || 'traditionalCompany',
            serviceCompany: !!data.serviceCompany,
            country: (data.country && typeof data.country === 'string') ? data.country.toUpperCase() : 'FR'
          };
          const company = data.company;
          if (company && typeof company === 'string') {
            const companyTrimmed = company.trim();
            attrsMap[normalizeKeyForLookup(companyTrimmed)] = attrs;
            chapeauMap[normalizeKeyForLookup(companyTrimmed)] = companyTrimmed;
          }
          const branch = data.branch;
          if (Array.isArray(branch)) {
            branch.forEach((b) => {
              if (b && typeof b === 'string') {
                const bTrimmed = b.trim();
                attrsMap[normalizeKeyForLookup(bTrimmed)] = attrs;
                chapeauMap[normalizeKeyForLookup(bTrimmed)] = (company && typeof company === 'string') ? company.trim() : bTrimmed;
              }
            });
          }
        });
        // Construction de la liste des offres à partir de jobOffers
        // (company, score, position peuvent être dans resultContent si non au niveau racine)
        const offers = [];
        const locationsSet = new Set();

        jobOffersSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          let company = data.company;
          let finalCompany = data.finalCompany;
          let score = data.score;
          let location = data.location;
          let position = data.position;
          let url = data.url;

          if (company === undefined || company === null || company === '') {
            try {
              const content = data.resultContent;
              const parsed = typeof content === 'string' ? JSON.parse(content) : content;
              company = parsed?.company;
              if (finalCompany === undefined && parsed?.finalCompany) finalCompany = parsed.finalCompany;
              if (score === undefined && parsed?.score != null) score = parsed.score;
              if (location === undefined && parsed?.location) location = parsed.location;
              if (position === undefined && parsed?.position) position = parsed.position;
            } catch {
              return;
            }
          } else {
            try {
              const content = data.resultContent;
              const parsed = typeof content === 'string' ? JSON.parse(content) : content;
              if (position === undefined && parsed?.position) position = parsed.position;
              if (finalCompany === undefined && parsed?.finalCompany) finalCompany = parsed.finalCompany;
            } catch {
              // ignore
            }
          }

          if (!company || typeof company !== 'string' || !company.trim()) return;
          if (score == null || (typeof score !== 'number' && typeof score !== 'string')) return;

          const numScore = typeof score === 'string' ? parseFloat(score) : Number(score);
          if (Number.isNaN(numScore) || numScore < 1 || numScore > 5) return;

          const loc = (location && typeof location === 'string') ? location.trim().toUpperCase() : 'FR';
          if (loc) locationsSet.add(loc === 'REMOTE' ? 'remote' : loc);

          const companyTrimmed = company.trim();
          const finalCompanyTrimmed = (finalCompany && typeof finalCompany === 'string' && finalCompany.trim())
            ? finalCompany.trim() : companyTrimmed;
          const attrsCompany = attrsMap[normalizeKeyForLookup(companyTrimmed)];
          const attrsFinal = attrsMap[normalizeKeyForLookup(finalCompanyTrimmed)];
          // CAC40 / French Tech : comptabiliser si company OU finalCompany correspond (chapeau ou filiale)
          const attrs = attrsCompany || attrsFinal ? {
            CAC40: !!(attrsCompany?.CAC40 || attrsFinal?.CAC40),
            FrenchTechNext40120: !!(attrsCompany?.FrenchTechNext40120 || attrsFinal?.FrenchTechNext40120),
            companyType: (attrsFinal || attrsCompany)?.companyType || 'traditionalCompany',
            serviceCompany: !!(attrsCompany?.serviceCompany || attrsFinal?.serviceCompany),
            country: (attrsFinal || attrsCompany)?.country || 'FR'
          } : null;
          const companyCountry = attrs?.country;
          // Chapeau CAC40 / French Tech : celui de l'entreprise qui appartient au groupe (pas l'autre)
          const chapeauCompany = chapeauMap[normalizeKeyForLookup(companyTrimmed)];
          const chapeauFinal = chapeauMap[normalizeKeyForLookup(finalCompanyTrimmed)];
          const chapeauCAC40 = (attrsCompany?.CAC40 ? chapeauCompany : null) ||
            (attrsFinal?.CAC40 ? chapeauFinal : null) || chapeauCompany || chapeauFinal;
          const chapeauFrenchTech = (attrsCompany?.FrenchTechNext40120 ? chapeauCompany : null) ||
            (attrsFinal?.FrenchTechNext40120 ? chapeauFinal : null) || chapeauCompany || chapeauFinal;
          const chapeau = chapeauCAC40 || chapeauFrenchTech || finalCompanyTrimmed || companyTrimmed;

          offers.push({
            company: companyTrimmed,
            finalCompany: finalCompanyTrimmed,
            displayName: companyTrimmed,
            chapeau,
            chapeauCAC40: chapeauCAC40 || null,
            chapeauFrenchTech: chapeauFrenchTech || null,
            chapeauOfCompany: chapeauCompany || null,
            chapeauOfFinalCompany: chapeauFinal || null,
            position: position && typeof position === 'string' ? position.trim() : '',
            url: url && typeof url === 'string' ? url.trim() : '',
            score: numScore,
            location: loc === 'REMOTE' ? 'remote' : loc,
            companyAttrs: attrs || null,
            companyCountry
          });
        });

        const locs = Array.from(locationsSet).sort((a, b) => {
          if (a === 'remote') return 1;
          if (b === 'remote') return -1;
          return a.localeCompare(b);
        });
        setAvailableLocations(locs);
        setRawOffers(offers);
      } catch (err) {
        console.error('ClassementEntreprises fetch error:', err);
        setError(err?.message || t('ClassementError'));
        setRawOffers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAndRank();
  }, [t]);

  /**
   * Filtrage, déduplication et agrégation par entreprise.
   * Les offres sans URL (ex. copier-coller) sont exclues.
   */
  useEffect(() => {
    if (rawOffers.length === 0) {
      setCompanies([]);
      return;
    }
    // Exclure les offres sans URL (issues du copier-coller sur la page d'accueil)
    let filtered = rawOffers.filter((o) => o.url && typeof o.url === 'string' && o.url.trim().length > 0);
    if (selectedCompanyType) {
      filtered = filtered.filter((o) => {
        // Filtrer par type (CAC40, French Tech, startup, ESN, etc.)
        const a = o.companyAttrs;
        if (!a) return false;
        if (selectedCompanyType === 'CAC40') return a.CAC40;
        if (selectedCompanyType === 'FrenchTechNext40120') return a.FrenchTechNext40120;
        if (selectedCompanyType === 'startup') return a.companyType === 'startup';
        if (selectedCompanyType === 'traditionalCompany') return a.companyType === 'traditionalCompany';
        if (selectedCompanyType === 'serviceCompany') return a.serviceCompany;
        return true;
      });
    }
    if (selectedFilter) {
      if (selectedFilter.startsWith('continent:')) {
        const continent = selectedFilter.replace('continent:', '');
        filtered = filtered.filter((o) => getContinentForCountry(o.location) === continent);
      } else {
        filtered = filtered.filter((o) => o.location === selectedFilter);
      }
    }

    const isFrenchForPerte = (o) => o.location === 'FR' || (o.location === 'remote' && o.companyCountry === 'FR');

    // === DÉDUPLICATION ===
    // Slot = (company, position, finalCompany). On déduplique au sein d'un slot.
    const groupByChapeau = selectedCompanyType === 'CAC40' || selectedCompanyType === 'FrenchTechNext40120';
    const getGroupKey = (o) => {
      if (selectedCompanyType === 'CAC40' && o.chapeauCAC40) return normalizeCompanyKey(o.chapeauCAC40);
      if (selectedCompanyType === 'FrenchTechNext40120' && o.chapeauFrenchTech) return normalizeCompanyKey(o.chapeauFrenchTech);
      return normalizeCompanyKey(o.company);
    };
    const companyKey = (o) => normalizeCompanyKey(o.company);
    const finalCompanyKey = (o) => normalizeCompanyKey(o.finalCompany || o.company);

    // 1) Par slot : compter les URLs uniques par host (wttj, hellowork, etc.)
    const urlsByHostBySlot = new Map();
    filtered.forEach((o) => {
      const cKey = groupByChapeau ? getGroupKey(o) : companyKey(o);
      const posKey = normalizePositionKey(o.position || '') || '__np__';
      const compKey = companyKey(o);
      const finKey = finalCompanyKey(o);
      const mapKey = cKey + '\x01' + posKey + '\x01' + compKey + '\x01' + finKey;
      const host = getHostFromUrl(o.url || '');
      const urlNorm = normalizeUrlForDedup(o.url || '');
      if (host && urlNorm) {
        if (!urlsByHostBySlot.has(mapKey)) urlsByHostBySlot.set(mapKey, new Map());
        const perHost = urlsByHostBySlot.get(mapKey);
        if (!perHost.has(host)) perHost.set(host, new Set());
        perHost.get(host).add(urlNorm);
      }
    });

    // 2) Si un slot a plusieurs hosts (ex. WTTJ + HelloWork), on garde le host avec le plus d'URLs distinctes
    // (ex. 3 sur WTTJ + 2 sur HW → on compte les 3 WTTJ, pas 1)
    const bestHostBySlot = new Map();
    urlsByHostBySlot.forEach((perHost, mapKey) => {
      if (perHost.size <= 1) return;
      let bestHost = '';
      let bestCount = 0;
      perHost.forEach((urlSet, host) => {
        if (urlSet.size > bestCount) {
          bestCount = urlSet.size;
          bestHost = host;
        }
      });
      if (bestHost) bestHostBySlot.set(mapKey, bestHost);
    });

    // 3) Si chaque host n'a qu'une URL (ex. 1 WTTJ + 1 HW), on fusionne → 1 offre (doute)
    const shouldCollapseCrossHost = (mapKey) => {
      const perHost = urlsByHostBySlot.get(mapKey);
      if (!perHost || perHost.size <= 1) return false;
      const counts = Array.from(perHost.values()).map((s) => s.size);
      return counts.every((n) => n === 1);
    };

    // 4) Agrégation : on ne garde que les offres du bestHost si multi-host, sinon toutes
    const byCompany = new Map();
    filtered.forEach((o) => {
      const key = groupByChapeau ? getGroupKey(o) : companyKey(o);
      const positionKey = normalizePositionKey(o.position || '') || '__np__';
      const compKey = companyKey(o);
      const finKey = finalCompanyKey(o);
      const mapKey = key + '\x01' + positionKey + '\x01' + compKey + '\x01' + finKey;
      const bestHost = bestHostBySlot.get(mapKey);
      if (bestHost && getHostFromUrl(o.url || '') !== bestHost) return; // Ignorer les offres des autres hosts

      const groupChapeau = groupByChapeau
        ? (selectedCompanyType === 'CAC40' ? o.chapeauCAC40 : o.chapeauFrenchTech) || o.chapeau
        : o.displayName;
      const displayName = groupByChapeau ? groupChapeau : o.displayName;
      const isFrench = isFrenchForPerte(o);
      const companyIsFilialeOfGroup = groupByChapeau && groupChapeau &&
        (o.chapeauOfCompany === groupChapeau) && o.company.trim() !== (groupChapeau || '').trim();
      const finalCompanyIsFilialeOfGroup = groupByChapeau && groupChapeau &&
        (o.chapeauOfFinalCompany === groupChapeau) && o.finalCompany.trim() !== (groupChapeau || '').trim();

      if (!byCompany.has(key)) {
        byCompany.set(key, {
          displayName,
          chapeau: groupChapeau || o.chapeau,
          totalScore: 0,
          count: 0,
          scores: [],
          frenchScores: [],
          filiales: new Set(),
          seenPositionKeys: new Set(),
          offers: []
        });
      }
      const existing = byCompany.get(key);
      const collapse = shouldCollapseCrossHost(mapKey);
      const urlNorm = normalizeUrlForDedup(o.url || '');
      const baseDedup = positionKey + '\x01' + compKey + '\x01' + finKey;
      const dedupKey = collapse ? baseDedup : (baseDedup + '\x01' + (urlNorm || '__no_url__'));
      if (existing.seenPositionKeys.has(dedupKey)) return; // Doublon : même (position, company, finalCompany, url)
      existing.seenPositionKeys.add(dedupKey);

      if (!existing.chapeau && (groupChapeau || o.chapeau)) existing.chapeau = groupChapeau || o.chapeau;
      existing.totalScore += o.score;
      existing.count += 1;
      existing.scores.push(o.score);
      if (isFrench) existing.frenchScores.push(o.score);
      if (displayName.length > (existing.displayName?.length || 0)) {
        existing.displayName = displayName;
      }
      if (companyIsFilialeOfGroup) existing.filiales.add(o.company.trim());
      if (finalCompanyIsFilialeOfGroup) existing.filiales.add(o.finalCompany.trim());
      existing.offers.push({ position: o.position || '', url: o.url || '' });
    });

    // Agrégation finale : moyenne des scores, calcul des pertes pour les offres FR en D/E
    const ranked = Array.from(byCompany.values())
          .map((c) => {
            const avgScore = c.totalScore / c.count;
            const roundedAvg = Math.round(avgScore);
            const frenchScores = c.frenchScores || [];
            const isDE = roundedAvg <= 2;
            const hasFrenchOffers = frenchScores.length > 0;

            const deScores = frenchScores.filter((s) => Math.round(s) <= 2);
            const N = deScores.length; // nb d'offres d'emploi FR notées D ou E
            let perteSalarie = null;
            let perteEtat = null;
            // Pertes calculées uniquement pour les offres françaises notées D ou E (même pondération D/E pour les deux)
            // perteSalarie : moyenne des pertes par offre (pas une somme)
            // perteEtat : total pour l'entreprise (base startup + part par offre)
            if (hasFrenchOffers && isDE && N > 0) {
              const frenchAvg = frenchScores.reduce((a, b) => a + b, 0) / frenchScores.length;
              const weight = getScoreWeight(frenchAvg); // E = 1, D = 0.8
              perteSalarie = BASE_PERTE_SALARIE * weight; // moyenne par offre
              perteEtat = (ETAT_BASE_PER_STARTUP + ETAT_PER_OFFER * N) * weight;
            }

            const chapeauForPromo = groupByChapeau ? c.displayName : (c.chapeau || c.displayName);
            const promoKey = chapeauForPromo ? normalizeKeyForLookup(chapeauForPromo) : '';
            const promo = promotionMap[promoKey];
            const rawLinks = (promo && promo.promoteResponsible && promo.links && promo.links.length) ? promo.links : [];
            const promoteResponsibleLinks = (rawLinks || []).filter(
              (l) => l && typeof l === 'object' && l.url
            );

            return {
              name: c.displayName,
              avgScore,
              count: c.count,
              offers: c.offers || [],
              perteSalarie,
              perteEtat,
              filialesTooltip: groupByChapeau
                ? [c.displayName, ...Array.from(c.filiales || []).sort()].join(', ')
                : null,
              promoteResponsible: promo ? promo.promoteResponsible : false,
              promoteResponsibleLinks,
            };
          })
          .filter((c) => c.count >= 1)
          .sort((a, b) => b.avgScore - a.avgScore);

    setCompanies(ranked);
  }, [rawOffers, selectedFilter, selectedCompanyType, promotionMap]);

  useEffect(() => {
    setCurrentPage(1);
  }, [companies.length, selectedFilter, selectedCompanyType]);

  // CAC40 et French Tech disponibles uniquement pour le filtre France
  useEffect(() => {
    if (selectedFilter !== 'FR' && (selectedCompanyType === 'CAC40' || selectedCompanyType === 'FrenchTechNext40120')) {
      setSelectedCompanyType('');
    }
  }, [selectedFilter, selectedCompanyType]);

  const offersForFR = rawOffers.filter((o) => o.location === 'FR');
  const hasFrenchTechOffers = offersForFR.some((o) => o.companyAttrs?.FrenchTechNext40120);

  const getFilterLabel = (value) => {
    if (!value) return t('ClassementFilterAllCountries');
    if (value === 'remote') return 'Remote';
    if (value.startsWith('continent:')) {
      const c = value.replace('continent:', '');
      return t(`ClassementContinent_${c}`) || c;
    }
    return getLocationLabel(value);
  };

  const scoreToLetter = (avg) => {
    const rounded = Math.round(avg);
    const clamped = Math.max(1, Math.min(5, rounded));
    return NUTRI_SCORE_LABELS[clamped] || '-';
  };

  if (loading) {
    return (
      <div className="classement-entreprises">
        <h2 className="classement-title">{t('ClassementTitle')}</h2>
        <p className="classement-loading">{t('ClassementLoading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="classement-entreprises">
        <h2 className="classement-title">{t('ClassementTitle')}</h2>
        <p className="classement-error">{error}</p>
      </div>
    );
  }

  const showPromoteCol = selectedCompanyType === 'CAC40' || selectedCompanyType === 'FrenchTechNext40120';
  const showIntermediaryGainCol = selectedCompanyType === 'serviceCompany';
  const isDoubtfulScore = (c) => Math.round(c.avgScore) <= 2;
  const totalIntermediaryGain = showIntermediaryGainCol
    ? companies.reduce(
        (sum, c) =>
          sum + (isDoubtfulScore(c) && c.count ? AVERAGE_INTERMEDIARY_COMMISSION * c.count : 0),
        0,
      )
    : 0;
  // Pagination : 20 entrées par page
  const totalPages = Math.max(1, Math.ceil(companies.length / PAGE_SIZE));
  const paginatedCompanies = companies.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const rankOffset = (currentPage - 1) * PAGE_SIZE;
  const isFrench = typeof i18n.language === 'string' && i18n.language.toLowerCase().startsWith('fr');
  const faqHref = isFrench ? '/fr/faq.html' : '/en/faq.html';

  return (
    <div className="classement-entreprises">
      <h2 className="classement-title">{t('ClassementTitle')}</h2>
      <p className="classement-data-source">
        <em>{t('ClassementDataSource')}</em>
      </p>
      <p className="classement-disclaimer">
        <em>
          {t('ClassementDisclaimerEstimates')}{' '}
          {isFrench ? (
            <>
              Voir la{' '}
              <a href={faqHref} className="classement-faq-link">
                FAQ
              </a>{' '}
              pour la méthodologie.
            </>
          ) : (
            <>
              See the{' '}
              <a href={faqHref} className="classement-faq-link">
                FAQ
              </a>{' '}
              for the methodology.
            </>
          )}
        </em>
      </p>

      <div className="classement-filters">
      {availableLocations.length > 0 && (
        <div className="classement-filter-row classement-location-filter">
          <div className="classement-dropdown-wrapper">
            <button
              type="button"
              className="classement-dropdown-trigger"
              onClick={() => setDropdownOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
            >
              <span className="classement-dropdown-selected">
                {selectedFilter && !selectedFilter.startsWith('continent:') ? (
                  <>
                    <span className="classement-filter-flag">
                      <LocationFlagIcon code={selectedFilter} />
                    </span>
                    <span>{getFilterLabel(selectedFilter)}</span>
                  </>
                ) : (
                  <span>{getFilterLabel(selectedFilter)}</span>
                )}
              </span>
              <span className="classement-dropdown-arrow">▼</span>
            </button>
            {dropdownOpen && (
              <ul className="classement-dropdown-list" role="listbox">
                <li role="option" aria-selected={!selectedFilter}>
                  <button type="button" onClick={() => handleLocationFilter('')}>
                    {t('ClassementFilterAllCountries')}
                  </button>
                </li>
                {CONTINENT_IDS.filter((c) => availableLocations.some((loc) => loc !== 'remote' && getContinentForCountry(loc) === c)).map((continent) => (
                  <li key={`continent:${continent}`} role="option" aria-selected={selectedFilter === `continent:${continent}`}>
                    <button
                      type="button"
                      onClick={() => handleLocationFilter(`continent:${continent}`)}
                      className={selectedFilter === `continent:${continent}` ? 'selected' : ''}
                    >
                      {t(`ClassementContinent_${continent}`)}
                    </button>
                  </li>
                ))}
                {availableLocations.filter((loc) => loc !== 'remote').sort((a, b) => a.localeCompare(b)).map((loc) => (
                  <li key={loc} role="option" aria-selected={selectedFilter === loc}>
                    <button
                      type="button"
                      onClick={() => handleLocationFilter(loc)}
                      className={selectedFilter === loc ? 'selected' : ''}
                    >
                      <LocationFlagIcon code={loc} />
                      <span>{getLocationLabel(loc)}</span>
                    </button>
                  </li>
                ))}
                {availableLocations.includes('remote') && (
                  <li role="option" aria-selected={selectedFilter === 'remote'}>
                    <button
                      type="button"
                      onClick={() => handleLocationFilter('remote')}
                      className={selectedFilter === 'remote' ? 'selected' : ''}
                    >
                      <LocationFlagIcon code="remote" />
                      <span>Remote</span>
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
          {dropdownOpen && (
            <div
              className="classement-dropdown-backdrop"
              role="presentation"
              onClick={() => setDropdownOpen(false)}
            />
          )}
        </div>
      )}
        <div className="classement-filter-row">
          <select
            id="classement-company-type"
            className="classement-select"
            value={selectedCompanyType}
            onChange={handleCompanyTypeChange}
          >
            <option value="">{t('ClassementFilterAllTypes')}</option>
            {selectedFilter === 'FR' && (
              <>
                <option value="CAC40">{t('ClassementFilterCAC40')}</option>
                {hasFrenchTechOffers && (
                  <option value="FrenchTechNext40120">{t('ClassementFilterFrenchTech')}</option>
                )}
              </>
            )}
            <option value="startup">{t('ClassementFilterStartup')}</option>
            <option value="traditionalCompany">{t('ClassementFilterTraditionalCompany')}</option>
            <option value="serviceCompany">{t('ClassementFilterServiceCompany')}</option>
          </select>
        </div>
      </div>

      {selectedCompanyType === 'FrenchTechNext40120' && (
        <p className="classement-french-tech-notice">
          <em>{t('ClassementFrenchTechNotice')}</em>
        </p>
      )}

      {companies.length === 0 ? (
        <p className="classement-empty">{t('ClassementEmpty')}</p>
      ) : (
        <div className="classement-table-wrapper">
          <table className="classement-table">
            <thead>
              <tr>
                <th className="classement-th-rank">#</th>
                <th className="classement-th-company">{t('ClassementCompany')}</th>
                <th className="classement-th-score">{t('ClassementScore')}</th>
                <th className="classement-th-count">{t('ClassementOffersCount')}</th>
                {showPromoteCol && (
                  <th className="classement-th-promote" title={t('ClassementPromoteResponsibleTooltip')}>
                    {t('ClassementPromoteResponsible')}
                  </th>
                )}
                <th className="classement-th-perte" title={t('ClassementPerteTooltip')}>{t('ClassementPerteSalarie')}</th>
                <th className="classement-th-perte" title={t('ClassementPerteTooltip')}>{t('ClassementPerteEtat')}</th>
                {showIntermediaryGainCol && (
                  <th
                    className="classement-th-perte"
                    title={t('ClassementGainIntermediaireTooltip')}
                  >
                    {t('ClassementGainIntermediaire')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedCompanies.map((c, idx) => (
                <tr key={c.name + idx}>
                  <td className="classement-td-rank">{rankOffset + idx + 1}</td>
                  <td className="classement-td-company" title={c.filialesTooltip || undefined}>
                    {c.name}
                  </td>
                  <td className="classement-td-score">
                    <NutriScoreBadge score={c.avgScore} size={40} />
                    <span className="classement-avg-value">
                      {c.avgScore.toFixed(2)}/5
                    </span>
                  </td>
                  <td className="classement-td-count">
                    <button
                      type="button"
                      className="classement-offers-link"
                      onClick={() => handleOffersDialogOpen(c.name, c.offers || [])}
                      title={t('ClassementOffersModalTitle', { company: c.name })}
                    >
                      {c.count}
                    </button>
                  </td>
                  {showPromoteCol && (
                    <td className="classement-td-promote">
                      {c.promoteResponsible && (c.promoteResponsibleLinks || []).length > 0 ? (
                        <button
                          type="button"
                          className="classement-promote-link-btn"
                          onClick={() => handlePromoteDialogOpen(c.name, c.promoteResponsibleLinks || [])}
                        >
                          {t('ClassementOui')}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  )}
                  <td className="classement-td-perte" title={c.perteSalarie == null ? t('ClassementPerteComingSoon') : undefined}>
                    {c.perteSalarie != null ? formatEuro(c.perteSalarie) : '—'}
                  </td>
                  <td className="classement-td-perte" title={c.perteEtat == null ? t('ClassementPerteComingSoon') : undefined}>
                    {c.perteEtat != null ? formatEuro(c.perteEtat) : '—'}
                  </td>
                  {showIntermediaryGainCol && (
                    <td className="classement-td-perte">
                      {isDoubtfulScore(c) && c.count
                        ? formatEuro(AVERAGE_INTERMEDIARY_COMMISSION * c.count)
                        : '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="classement-footer">
                <td
                  colSpan={4}
                  className="classement-td-total-label"
                  title={showIntermediaryGainCol ? undefined : t('ClassementPerteTooltip')}
                >
                  {showIntermediaryGainCol ? t('ClassementTotals') : t('ClassementTotalPerteEtat')}
                </td>
                {showPromoteCol && <td className="classement-td-promote" />}
                <td className="classement-td-perte" title={t('ClassementPerteComingSoon')}>—</td>
                <td
                  className="classement-td-perte classement-td-total"
                  title={companies.every((c) => c.perteEtat == null) ? t('ClassementPerteComingSoon') : undefined}
                >
                  {companies.some((c) => c.perteEtat != null)
                    ? formatEuro(companies.filter((c) => c.perteEtat != null).reduce((sum, c) => sum + c.perteEtat, 0))
                    : '—'}
                </td>
                {showIntermediaryGainCol && (
                  <td
                    className="classement-td-perte classement-td-total"
                    title={totalIntermediaryGain === 0 ? t('ClassementPerteComingSoon') : undefined}
                  >
                    {totalIntermediaryGain > 0 ? formatEuro(totalIntermediaryGain) : '—'}
                  </td>
                )}
              </tr>
            </tfoot>
          </table>
          {companies.length > PAGE_SIZE && (
            <nav className="classement-pagination" aria-label={t('ClassementPaginationPage', { current: currentPage, total: totalPages })}>
              <button
                type="button"
                className="classement-pagination-btn"
                onClick={handlePagePrev}
                disabled={currentPage <= 1}
              >
                {t('ClassementPaginationPrev')}
              </button>
              <span className="classement-pagination-info">
                {t('ClassementPaginationPage', { current: currentPage, total: totalPages })}
              </span>
              <button
                type="button"
                className="classement-pagination-btn"
                onClick={handlePageNext}
                disabled={currentPage >= totalPages}
              >
                {t('ClassementPaginationNext')}
              </button>
            </nav>
          )}
        </div>
      )}

      {companies.length > 0 && (
        <div className="classement-changeons-ca-row">
          <a
            href="https://www.linkedin.com/pulse/our-big-techs-regulation-innovation-professions-europe-giraudel-r7bve/"
            target="_blank"
            rel="noopener noreferrer"
            className="classement-changeons-ca-link"
            onClick={() => logEvent(analytics, 'classement_changeons_ca_click', {})}
          >
            {t('PrixMalbouffeChangeonsCa')}
          </a>
        </div>
      )}

      {offersDialogOpen && offersDialogData && (
        <div
          className="classement-promote-dialog-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="classement-offers-dialog-title"
          onClick={handleOffersDialogClose}
        >
          <div
            className="classement-promote-dialog classement-offers-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="classement-offers-dialog-title" className="classement-promote-dialog-title">
              {t('ClassementOffersModalTitle', { company: offersDialogData.company })}
            </h3>
            <ul className="classement-offers-dialog-list">
              {offersDialogData.offers.map((offer, i) => (
                <li key={i} className="classement-offers-dialog-item">
                  <span className="classement-offers-dialog-position">{offer.position || '—'}</span>
                  <a
                    href={offer.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="classement-offers-dialog-link"
                  >
                    {offer.url}
                  </a>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="classement-promote-dialog-close"
              onClick={handleOffersDialogClose}
            >
              {t('ClassementClose')}
            </button>
          </div>
        </div>
      )}

      {promoteDialogOpen && promoteDialogData && (
        <div
          className="classement-promote-dialog-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="classement-promote-dialog-title"
          onClick={handlePromoteDialogClose}
        >
          <div
            className="classement-promote-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="classement-promote-dialog-title" className="classement-promote-dialog-title">
              {t('ClassementPromoteResponsible')} — {promoteDialogData.company}
            </h3>
            <ul className="classement-promote-dialog-list">
              {promoteDialogData.links.map((link, i) => (
                <li key={i}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="classement-promote-dialog-link"
                    onClick={() => handlePromoteLinkClick(promoteDialogData.company, link.url)}
                  >
                    <span className="classement-promote-dialog-link-name">{link.name}</span>
                    <span className="classement-promote-dialog-link-url">{link.url}</span>
                  </a>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="classement-promote-dialog-close"
              onClick={handlePromoteDialogClose}
            >
              {t('ClassementClose')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClassementEntreprises;
